/* eslint-disable react/no-unstable-nested-components */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useMemo, useRef } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ENVIRONMENT } from 'config';
import { useLocation } from 'react-router-dom';
import { Copy, Share } from 'react-feather';
import { consoleCommands } from 'modules/common/ui-controller';
import remarkGfm from 'remark-gfm';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AnimatePresence, motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { useSelector, useDispatch } from 'react-redux';
import isFeatureEnabled from 'modules/common/utils/string';
import {
  selectCurrentUrl,
  selectEncodedTabList,
  selectTabValue,
} from 'modules/dashboard/selectors';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { accordionStyles } from './style';
import ShareableUrl from './shareable-url';
import TerminalLog from './debug-log';
import { dashboardActions } from '../../../dashboard/slice';

/**
 * VI response list item component
 */
const ResponseItem = ({
  msg,
  index,
  expanded,
  page,
  anonymize,
  deanonymizeTitle,
  setCopied,
  setCopiedUrl,
  setExpanded,
}) => {
  const markdownRef = useRef(null);
  // texts of the response, the actual hotel names are replaced with the demo labels in demo mode.
  // The demo labels in the title are replaced with the actual hotel names out of demo mode
  const { title, response, userPrompt, systemPrompt } = useMemo(() => {
    const scrub = (text) => (anonymize ? anonymize(text) : text);
    const rawTitle = msg?.response?.title;
    return {
      title: scrub(deanonymizeTitle ? deanonymizeTitle(rawTitle) : rawTitle),
      response: scrub(msg?.response?.response),
      userPrompt: scrub(msg?.response?.userPrompt),
      systemPrompt: scrub(msg?.response?.systemPrompt),
    };
  }, [msg?.response, anonymize, deanonymizeTitle]);
  const dispatch = useDispatch();
  const currentShareUrl = useSelector(selectCurrentUrl);
  const organizationId = useSelector(selectOrganizationId);
  const encodedTabs = useSelector(selectEncodedTabList);
  const tabValue = useSelector(selectTabValue);
  const location = useLocation();
  //
  const handleChange = (panelIndex) => (event, isExpanded) => {
    setExpanded(isExpanded ? panelIndex : null);
  };
  //
  const getTruncatedText = (str = '') => {
    const words = str?.split(' ');
    if (words?.length <= 15) return str;
    return `${words?.slice(0, 15)?.join(' ')} ...` ?? '';
  };
  //
  const handleCopyRichText = async (e, titleText, responseText) => {
    e.stopPropagation();
    if (!markdownRef.current) return;
    try {
      const originalPlainText = markdownRef.current.textContent ?? responseText;
      const plainTextWithTitle = `${titleText}\n====================\n\n${originalPlainText}`;
      let cleanHtmlContent = markdownRef.current.innerHTML;

      cleanHtmlContent = cleanHtmlContent.replaceAll('</p>', '</p><br />');
      cleanHtmlContent = cleanHtmlContent.replaceAll('</div>', '</div><br />');
      const htmlTextWithTitle = `
      <div style="font-family: sans-serif; font-size: 1rem; font-weight: 600; color: #111827; margin-bottom: 4px;">
       <strong> ${titleText}</strong>
      </div>
      <br />
      <div style="font-family: sans-serif; font-size: 1rem; font-weight: normal; color: #374151;">
        ${cleanHtmlContent}
      </div>
    `;
      const blobPlain = new Blob([plainTextWithTitle], { type: 'text/plain' });
      const blobHtml = new Blob([htmlTextWithTitle], { type: 'text/html' });

      const clipboardItem = new ClipboardItem({
        'text/plain': blobPlain,
        'text/html': blobHtml,
      });
      await navigator.clipboard.write([clipboardItem]);

      setCopied(true);
    } catch (err) {
      console.error('Failed to copy formatted text: ', err?.message);
    }
  };
  //
  const generateShareVIResponse = async (e) => {
    e.stopPropagation();
    if (!msg?.id) return;
    if(msg?.isShareable === undefined || msg?.isShareable === false){
      dispatch(dashboardActions.updateViResponse({
        isShareable: true,
        organizationId,
        responseId: msg.id,     
      }))
    }
    const domain =
      window.location.origin === ENVIRONMENT.APP_URL
        ? ENVIRONMENT.APP_URL
        : ENVIRONMENT.AIRO_APP_URL;
    const updatedUrl = `${domain}${location?.pathname}?${currentShareUrl}&${consoleCommands.VI}=${msg?.shareId}#${encodedTabs[tabValue]}`;

    const blobPlain = new Blob([updatedUrl], { type: 'text/plain' });

    const clipboardItem = new ClipboardItem({
      'text/plain': blobPlain,
    });
    await navigator.clipboard.write([clipboardItem]);
    setCopiedUrl(true);
  };
  //
  const panelColor = msg?.isShareResult ? '#FDF9F0' : '#F6F8FB';
  const borderColor = msg?.isShareResult ? '#F9C82A' : '#335BEB';
  const iconColor = msg?.isShareResult ? '#EBAE2B' : '#3779E9';
  //
  return (
    <Accordion
      key={msg.shareId}
      disableGutters
      expanded={expanded === index}
      onChange={handleChange(index)}
      elevation={0}
      sx={{
        ...accordionStyles,
        backgroundColor: expanded !== index ? panelColor : 'white',
        borderColor,
        borderLeftWidth: 6,
      }}
    >
      <Tooltip
        title={msg?.isShareResult ? 'This session has been loaded via Share URL.' : ''}
        placement="bottom"
        arrow
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel${index}-content`}
          id={`panel${index}-header`}
          sx={{ '& .MuiAccordionSummary-content': { minWidth: 0 } }}
        >
          <Stack flexDirection="column" sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 'bold' }}>
              {getTruncatedText(title) ?? getTruncatedText(response)}
            </Typography>
            <Typography fontSize={10} variant="caption" sx={{ color: 'black' }}>
              {msg?.response?.requestedTime ?? ''}
            </Typography>
            {expanded !== index && (
              <ShareableUrl msg={msg?.response} responseId={msg?.id} page={page} />
            )}
          </Stack>
          <Tooltip title="Copy Report">
            <span style={{ display: 'inline-block', flexShrink: 0 }}>
              <IconButton size="small" onClick={(e) => handleCopyRichText(e, title, response)}>
                <Copy
                  size={16}
                  style={{
                    color: iconColor,
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
          {isFeatureEnabled(ENVIRONMENT.ENABLE_VI_SHARE_FEATURE) && (
            <Tooltip title="Share Report">
              <span style={{ display: 'inline-block', flexShrink: 0 }}>
                <IconButton size="small" onClick={(e) => generateShareVIResponse(e)}>
                  <Share
                    size={16}
                    style={{
                      color: iconColor,
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </AccordionSummary>
      </Tooltip>
      <AccordionDetails sx={{ borderTop: '1px solid #eee' }}>
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div ref={markdownRef}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: (props) => <p style={{ paddingTop: 10, margin: 0 }} {...props} />,

                  table: (props) => (
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        style={{
                          width: 'auto',
                          borderCollapse: 'collapse',
                          border: '1px solid #d1d5db',
                          borderRadius: '5px',
                        }}
                        {...props}
                      />
                    </div>
                  ),
                  th: ({ isHeader, ...props }) => (
                    <th
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '5px',
                        padding: '8px 16px',
                        textAlign: 'left',
                      }}
                      {...props}
                    />
                  ),
                  td: ({ isHeader, ...props2 }) => (
                    <td
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '5px',
                        padding: '8px 16px',
                        textAlign: 'left',
                      }}
                      {...props2}
                    />
                  ),
                }}
              >
                {response}
              </ReactMarkdown>
            </div>
          </motion.div>
        </AnimatePresence>
        {expanded === index && (
          <ShareableUrl msg={msg?.response} responseId={msg?.id} page={page} />
        )}
        {ENVIRONMENT.IS_DATA_EXPORT_INTERPRETER_ENABLED.toLowerCase() === 'false' &&
          expanded === index && (
            <TerminalLog logs={userPrompt}title="User Prompt" />
          )}
        {ENVIRONMENT.IS_DATA_EXPORT_INTERPRETER_ENABLED.toLowerCase() === 'false' &&
          expanded === index && (
            <TerminalLog logs={systemPrompt}title="System Prompt" />
          )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ResponseItem;
